import Link from 'next/link';
import styled, { css, useTheme } from 'styled-components';
import { BaseControlColor, hexToRgba } from '@/shared/modules/styled-system';
import { ButtonSize, ButtonTextColor, ButtonVariant } from './types';
export type ButtonElementProps = {
  $color: `${BaseControlColor}`;
  $size?: `${ButtonSize}`;
  $variant?: `${ButtonVariant}`;
  $textColor?: `${ButtonTextColor}`;
  $shadow?: boolean;
  $fluid?: boolean;
  $hasText?: boolean;
  $hasIcon?: boolean;
};
export const IconContent = styled.i`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0;
  width: 1rem;
  height: auto;

  & svg {
    width: 100%;
    height: auto;
  }
`;
export const TextContent = styled.span`
  display: flex;
  align-items: center;
  font-weight: 500;
  flex-shrink: 0;
`;
const sizeMixin = (props: ButtonElementProps) => {
  if (props.$size === ButtonSize.XS) {
    return css`
      padding: 0 0.75rem;
      min-height: 1.5rem;

      ${TextContent} {
        font-size: 0.75rem;
        line-height: 1.5rem;
        font-weight: 500;
      }
    `;
  }
  if (props.$size === ButtonSize.S) {
    return css`
      padding: 0 0.75rem;
      min-height: 1.75rem;

      ${TextContent} {
        font-size: 0.75rem;
        line-height: 1.75rem;
        font-weight: 600;
      }
    `;
  }
  if (props.$size === ButtonSize.M) {
    return css`
      padding: 0 1rem;
      min-height: 2rem;

      ${TextContent} {
        font-size: 0.8rem;
        line-height: 2rem;
        font-weight: 600;
      }
    `;
  }
  if (props.$size === ButtonSize.L) {
    return css`
      padding: 0 1.25rem;
      min-height: 2rem;

      ${TextContent} {
        font-size: 0.9rem;
        line-height: 2rem;
        font-weight: 600;
      }
    `;
  }
  if (props.$size === ButtonSize.XL) {
    return css`
      padding: 0 1.25rem;
      min-height: 2.5rem;
      border-radius: 0.4rem;

      ${TextContent} {
        font-size: 1rem;
        line-height: 2.5rem;
        font-weight: 600;
      }
    `;
  }
  if (props.$size === ButtonSize.XXL) {
    return css`
      padding: 0 1.25rem;
      min-height: 3rem;
      border-radius: 0.4rem;

      ${TextContent} {
        font-size: 1.3rem;
        line-height: 3rem;
        font-weight: 600;
      }
    `;
  }
  return css`
    padding: 0 0.25rem;
  `;
};
const textColorMixin = (props: ButtonElementProps) => {
  if (props.$textColor === ButtonTextColor.Ocean) {
    return css`
      color: ${({
      theme
    }) => theme.tokens.colors.link[100].base};

      &:hover,
      &:focus {
        color: ${({
      theme
    }) => theme.tokens.colors.link[100].hover};
      }

      &:disabled {
        color: ${({
      theme
    }) => theme.tokens.colors.link[100].base};
      }
    `;
  }
  if (props.$textColor === ButtonTextColor.Black) {
    return css`
      color: ${hexToRgba('#0D0D10', 0.9)};

      &:hover,
      &:focus {
        color: ${hexToRgba('#0D0D10', 0.9)};
      }

      &:disabled {
        color: ${hexToRgba('#0D0D10', 0.9)};
      }
    `;
  }
  if (props.$textColor === ButtonTextColor.Darkgrey) {
    return css`
      color: ${hexToRgba('#0D0D10', 0.7)};

      &:hover,
      &:focus {
        color: ${hexToRgba('#0D0D10', 0.7)};
      }

      &:disabled {
        color: ${hexToRgba('#0D0D10', 0.7)};
      }
    `;
  }
  if (props.$color === BaseControlColor.Transparent && props.$variant === ButtonVariant.Text) {
    return css`
      color: ${({
      theme
    }) => theme.tokens.colors.link[100].base};
      font-weight: 400;

      &:hover,
      &:focus {
        color: ${({
      theme
    }) => theme.tokens.colors.link[100].hover};
      }

      &:disabled {
        color: ${({
      theme
    }) => hexToRgba(theme.tokens.colors.link[100].base, 0.5)};
      }
    `;
  }
  return css``;
};
const ButtonStyles = (props: ButtonElementProps) => {
  const theme = useTheme();
  const buttonColor = theme.recipes.button[props.$color];
  return css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-radius: 0.3rem;
    border: none;
    cursor: pointer;
    position: relative;

    &:disabled {
      cursor: not-allowed;
    }

    & svg {
      vertical-align: middle;
    }

    background-image: ${() => buttonColor?.normal.backgroundImage};
    background-color: ${() => buttonColor?.normal.backgroundColor};
    color: ${() => buttonColor?.normal.text};

    & svg path:last-of-type,
    & svg path.glyph,
    & svg circle {
      fill: ${() => buttonColor?.normal.icon};
    }

    &:hover,
    &:focus {
      background-image: ${() => buttonColor?.hover.backgroundImage};
      background-color: ${() => buttonColor?.hover.backgroundColor};
      color: ${() => buttonColor?.hover.text};

      & svg path:last-of-type,
      & svg path.glyph,
      & svg circle {
        fill: ${() => buttonColor?.hover.icon};
      }
    }

    &:active {
      background-image: ${() => buttonColor?.active.backgroundImage};
      background-color: ${() => buttonColor?.active.backgroundColor};
      color: ${() => buttonColor?.active.text};

      & svg path:last-of-type,
      & svg path.glyph,
      & svg circle {
        fill: ${() => buttonColor?.active.icon};
      }
    }

    &:disabled {
      background-image: ${() => buttonColor?.disabled.backgroundImage};
      background-color: ${() => buttonColor?.disabled.backgroundColor};
      color: ${() => buttonColor?.disabled.text};
      box-shadow: none;

      & svg path:last-of-type,
      & svg path.glyph,
      & svg circle {
        fill: ${() => buttonColor?.disabled.icon};
      }
    }
  `;
};
const stylesMixin = (props: ButtonElementProps) => {
  if (props.$shadow) {
    return css`
      box-shadow: 0 0.5rem 1rem -0.5rem rgba(0, 0, 0, 0.3);
    `;
  }
  if (props.$fluid) {
    return css`
      width: 100%;
      padding: 0;
    `;
  }
  if (props.$hasText && props.$size === ButtonSize.L) {
    return css`
      min-width: 6.5rem;
    `;
  }
  if (props.$hasText && props.$size === ButtonSize.XL) {
    return css`
      min-width: 7.3rem;
    `;
  }
  if (props.$hasIcon && props.$hasText) {
    return css`
      padding: 0 1rem 0 0.75rem;

      ${IconContent} {
        margin-right: 0.5rem;
        margin-left: 0;
        opacity: 0.9;
      }

      ${props.$size === ButtonSize.XL && css`
        padding: 0 1.25rem 0 1rem;

        ${IconContent} {
          margin-right: 0.75rem;
        }
      `}
    `;
  }
  if (props.$hasIcon && !props.$hasText) {
    return css`
      padding: 0 0.5rem;
    `;
  }
  if (props.$variant === ButtonVariant.Rounded) {
    return css`
      border-radius: 10rem;
    `;
  }
  if (props.$variant === ButtonVariant.Text && props.$size === ButtonSize.L) {
    return css`
      padding: 0 0.5rem;
      min-height: 1.5rem;

      ${TextContent} {
        font-size: 0.75rem;
        line-height: 1.5rem;
      }
    `;
  }
  if (props.$variant === ButtonVariant.Text && props.$size === ButtonSize.XS) {
    return css`
      padding: 0 0.5rem;
      min-height: 1rem;

      ${TextContent} {
        font-size: 0.8rem;
        line-height: 1rem;
      }
    `;
  }
  if (props.$variant === ButtonVariant.Round) {
    return css`
      border-radius: 50%;

      ${props.$size === ButtonSize.L && css`
        width: 2rem;
        height: 2rem;

        ${props.$hasIcon && css`
          ${IconContent} {
            width: 1.5rem;
            height: 1.5rem;
            padding: 0;
          }
        `}
      `}
    `;
  }
};
export const ButtonElement = styled.button<ButtonElementProps>`
  ${props => ButtonStyles(props)}

  ${sizeMixin};

  ${textColorMixin};

  ${stylesMixin};
`;
export const ButtonLink = styled(Link)<ButtonElementProps>`
  ${props => ButtonStyles(props)}

  ${sizeMixin};

  ${textColorMixin};

  ${stylesMixin};
`;